import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// Brand palette (matches the navy/red theme used across the rest of the system)
const PALETTE = {
  red: '#AC2000',
  orange: '#F6630D',
  amber: '#F2B705',
  teal: '#2EC4B6',
  green: '#27AE60',
  blue: '#3B82C4',
  navy: '#11324D',
};

const FALLBACK_MONTH_LABELS = ['ينا', 'فبر', 'مار', 'أبر', 'ماي', 'يون', 'يول', 'أغس', 'سبت', 'أكت', 'نوف', 'ديس'];
const headcountTrend = [68, 72, 75, 78, 82, 88, 93, 97, 101, 108, 112, 118];
const DEPARTMENT_VALUES = [54, 28, 36, 22, 41];
const FALLBACK_DEPARTMENT_LABELS = ['الهندسة', 'الموارد البشرية', 'المحاسبة', 'المشتريات', 'المشاريع'];
const ATTENDANCE_BASE = [
  { key: 'attendance', value: 310, color: PALETTE.green },
  { key: 'leave', value: 54, color: PALETTE.amber },
  { key: 'absence', value: 28, color: PALETTE.red },
];
const HIRING_STATS = [
  { hires: 14, exits: 4 },
  { hires: 11, exits: 6 },
  { hires: 18, exits: 5 },
  { hires: 20, exits: 7 },
  { hires: 19, exits: 8 },
  { hires: 23, exits: 9 },
];
const satisfactionTrend = [72, 74, 76, 78, 77, 80, 83, 84, 82, 86, 88, 90];
const performanceTrend = [65, 66, 70, 72, 75, 76, 79, 81, 80, 82, 85, 87];
const WEEKLY_ATTENDANCE_DATA = [
  { dayAr: 'السبت', dayEn: 'Sat', attendance: 285, leave: 45, absence: 20 },
  { dayAr: 'الأحد', dayEn: 'Sun', attendance: 295, leave: 38, absence: 17 },
  { dayAr: 'الإثنين', dayEn: 'Mon', attendance: 310, leave: 35, absence: 15 },
  { dayAr: 'الثلاثاء', dayEn: 'Tue', attendance: 305, leave: 40, absence: 15 },
  { dayAr: 'الأربعاء', dayEn: 'Wed', attendance: 300, leave: 42, absence: 18 },
  { dayAr: 'الخميس', dayEn: 'Thu', attendance: 290, leave: 50, absence: 20 },
  { dayAr: 'الجمعة', dayEn: 'Fri', attendance: 0, leave: 0, absence: 0 },
];

const summaryCardStyle = {
  background: '#1b3350',
  borderRadius: '18px',
  border: '1px solid #2c4a70',
  padding: '18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  boxShadow: '0 12px 32px rgba(7, 11, 20, 0.35)',
};

const chartCardStyle = {
  background: '#1b3350',
  borderRadius: '20px',
  border: '1px solid #2c4a70',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: '0 18px 40px rgba(8, 12, 24, 0.4)',
};

const tooltipStyle = {
  backgroundColor: '#1a1f2e',
  border: '1px solid #2a3446',
  borderRadius: '12px',
  color: '#e2e8f0',
};

const HrDashboard = () => {
  const { t, i18n } = useTranslation('global');
  const [windowWidth, setWindowWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1280;
  const chartHeight = isMobile ? 240 : 300;
  const isRTL = i18n.language === 'ar';

  const languageMonthLabels = t('hrHome.monthLabels', { returnObjects: true });
  const monthLabels =
    Array.isArray(languageMonthLabels) && languageMonthLabels.length === FALLBACK_MONTH_LABELS.length
      ? languageMonthLabels
      : FALLBACK_MONTH_LABELS;

  const departmentLabels = t('hrHome.departmentLabels', { returnObjects: true });
  const normalizedDepartmentLabels =
    Array.isArray(departmentLabels) && departmentLabels.length === DEPARTMENT_VALUES.length
      ? departmentLabels
      : FALLBACK_DEPARTMENT_LABELS;

  const deptDataValues = normalizedDepartmentLabels.map((label, index) => ({
    name: label,
    employees: DEPARTMENT_VALUES[index] ?? 0,
  }));

  const attendanceData = ATTENDANCE_BASE.map((slice) => ({
    ...slice,
    label: t(`hrHome.charts.dailyAttendance.${slice.key}`),
  }));

  const headcountData = monthLabels.map((month, index) => ({
    month,
    headcount: headcountTrend[index] ?? 0,
  }));

  const totalAttendance = attendanceData.reduce((sum, slice) => sum + slice.value, 0);
  const attendancePercent = totalAttendance ? Math.round((attendanceData[0].value / totalAttendance) * 100) : 0;
  const attendanceMiniSeries = attendanceData.map((slice, index) => ({ index, value: slice.value }));

  const topDepartment = deptDataValues.reduce(
    (max, dept) => (dept.employees > max.employees ? dept : max),
    deptDataValues[0]
  );

  const hiringData = HIRING_STATS.map((item, index) => ({
    month: monthLabels[index] ?? monthLabels[0],
    hires: item.hires,
    exits: item.exits,
    net: item.hires - item.exits,
  }));

  const satisfactionData = monthLabels.map((month, index) => ({
    month,
    satisfaction: satisfactionTrend[index] ?? 0,
    performance: performanceTrend[index] ?? 0,
  }));

  const currentHeadcount = headcountTrend.at(-1) ?? 0;
  const totalHires = HIRING_STATS.reduce((acc, item) => acc + item.hires, 0);
  const totalExits = HIRING_STATS.reduce((acc, item) => acc + item.exits, 0);
  const netHiring = totalHires - totalExits;
  const satisfactionAverage = Math.round(
    satisfactionTrend.reduce((acc, val) => acc + val, 0) / satisfactionTrend.length
  );
  const performanceAverage = Math.round(
    performanceTrend.reduce((acc, val) => acc + val, 0) / performanceTrend.length
  );


  // summary charts data
  const summaryCharts = [
    {
      title: t('hrHome.statistics.totalEmployees'),
      value: currentHeadcount,
      color: PALETTE.red,
      data: headcountData,
      dataKey: 'headcount',
    },
    {
      title: t('hrHome.statistics.avgSatisfaction'),
      value: `${satisfactionAverage}%`,
      color: PALETTE.teal,
      data: satisfactionData,
      dataKey: 'satisfaction',
    },
    {
      title: t('hrHome.statistics.avgPerformance'),
      value: `${performanceAverage}%`,
      color: PALETTE.orange,
      data: satisfactionData,
      dataKey: 'performance',
    },
    {
      title: t('hrHome.statistics.netHiring'),
      value: netHiring >= 0 ? `+${netHiring}` : netHiring,
      color: PALETTE.blue,
      data: hiringData,
      dataKey: 'net',
    },
    {
      title: t('hrHome.statistics.avgAttendance'),
      value: `${attendancePercent}%`,
      color: PALETTE.amber,
      data: attendanceMiniSeries,
      dataKey: 'value',
    },
    {
      title: t('hrHome.statistics.largestDepartment'),
      value: `${topDepartment?.name ?? ''} (${topDepartment?.employees ?? 0})`,
      color: PALETTE.green,
      data: deptDataValues,
      dataKey: 'employees',
    },
  ];

  const chartColumns = isMobile ? 1 : isTablet ? 2 : 3;
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const textAlign = dir === 'rtl' ? 'right' : 'left';
  const alignItems = dir === 'rtl' ? 'flex-end' : 'flex-start';
  const chartTitleStyle = { margin: 0, fontSize: '20px', fontWeight: 700, textAlign };
  const chartSubtitleStyle = { margin: 0, fontSize: '14px', color: '#94a3b8', textAlign };

  const weeklyAttendanceData = WEEKLY_ATTENDANCE_DATA.map((item) => ({
    day: isRTL ? item.dayAr : item.dayEn,
    attendance: item.attendance,
    leave: item.leave,
    absence: item.absence,
  }));

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ 
        padding: isMobile ? '20px' : '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: '#f8fafc',
        boxSizing: 'border-box',
        direction: dir,
        textAlign,
      }}
    >
      <div
        style={{
          marginBottom: isMobile ? '18px' : '28px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          textAlign,
        }}
      >
        <h1
        style={{
            margin: 0,
            fontSize: isMobile ? '24px' : '29px',
            fontWeight: 'bold',
            fontFamily: 'Times New Roman, Times, serif, Montserrat-Arabic, sans-serif',
            textAlign,
          }}
        >
          {t('hrHome.title')}
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: '14px',
            color: '#94a3b8',
            fontFamily: 'Times New Roman, Times, serif, Montserrat-Arabic, sans-serif',
            fontWeight: 'bold',
          }}
        >
        </p>
          </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: isMobile ? '10px' : '16px',
          marginBottom: isMobile ? '16px' : '32px',
        }}
      >
        {summaryCharts.map((card) => (
          <div key={card.title} style={{ ...summaryCardStyle, textAlign, alignItems, padding: isMobile ? '14px' : '20px', gap: isMobile ? '8px' : '12px' }}>
          <div style={{ width: '100%' }}>
              <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#94a3b8', display: 'block', textAlign }}>{card.title}</span>
              <div style={{ fontSize: isMobile ? '21px' : '28px', fontWeight: 700, color: card.color, marginTop: '4px', textAlign }}>
                {card.value}
              </div>
            </div>
            <div style={{ height: isMobile ? 50 : 90 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={card.data}>
                  <defs>
                    <linearGradient id={`gradient-${card.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={card.color} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={card.color} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area
                    type="monotone"
                    dataKey={card.dataKey}
                    stroke={card.color}
                    fill={`url(#gradient-${card.dataKey})`}
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>

      <div
          style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '18px' : '24px',
        }}
      >
        <div style={chartCardStyle}>
          <div>
            <h2 style={chartTitleStyle}>{t('hrHome.charts.workforceEvolution.title')}</h2>
            <p style={chartSubtitleStyle}>{t('hrHome.charts.workforceEvolution.description')}</p>
          </div>
          <div style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={headcountData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                <XAxis dataKey="month" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="headcount" stroke={PALETTE.red} strokeWidth={2.4} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={chartCardStyle}>
          <div>
            <h2 style={chartTitleStyle}>{t('hrHome.charts.departmentDistribution.title')}</h2>
            <p style={chartSubtitleStyle}>{t('hrHome.charts.departmentDistribution.description')}</p>
            </div>
          <div style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptDataValues}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="employees" fill={PALETTE.green} radius={[12, 12, 12, 12]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div
        style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
          gap: isMobile ? '18px' : '24px',
          marginTop: isMobile ? '20px' : '28px',
        }}
      >
        <div style={{ ...chartCardStyle, gridColumn: (isMobile || isTablet) ? 'auto' : 'span 2' }}>
          <div>
            <h2 style={chartTitleStyle}>{t('hrHome.charts.hiringVsResignations.title')}</h2>
            <p style={chartSubtitleStyle}>{t('hrHome.charts.hiringVsResignations.description')}</p>
          </div>
          <div style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hiringData}>
                <defs>
                  <linearGradient id="hiresGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PALETTE.green} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={PALETTE.green} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="exitsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PALETTE.red} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={PALETTE.red} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                <XAxis dataKey="month" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ color: '#cbd5f5', fontSize: '13px' }} />
                <Area type="monotone" dataKey="hires" stroke={PALETTE.green} fill="url(#hiresGradient)" strokeWidth={2} />
                <Area type="monotone" dataKey="exits" stroke={PALETTE.red} fill="url(#exitsGradient)" strokeWidth={2} />
                <Line type="monotone" dataKey="net" stroke={PALETTE.blue} strokeWidth={2} dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={chartCardStyle}>
          <div>
            <h2 style={chartTitleStyle}>{t('hrHome.charts.dailyAttendance.title')}</h2>
            <p style={chartSubtitleStyle}>{t('hrHome.charts.dailyAttendance.description')}</p>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: '18px',
              height: chartHeight,
            }}
          >
            <ResponsiveContainer width={isMobile ? '100%' : '55%'} height="100%">
              <PieChart>
                <Tooltip contentStyle={{ ...tooltipStyle, color: '#ffffff' }} itemStyle={{ color: '#ffffff' }} labelStyle={{ color: '#ffffff' }} />
                <Pie
                  data={attendanceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 40 : 55}
                  outerRadius={isMobile ? 70 : 85}
                  dataKey="value"
                  paddingAngle={4}
                >
                  {attendanceData.map((entry) => (
                    <Cell key={entry.label} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: '1 1 auto' }}>
              <div style={{ fontSize: '34px', fontWeight: 700, color: PALETTE.green }}>{attendancePercent}%</div>
              <span style={{ fontSize: '14px', color: '#94a3b8' }}>{t('hrHome.charts.dailyAttendance.totalAttendance')}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {attendanceData.map((slice) => (
                  <div key={slice.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: slice.color }}>{slice.label}</span>
                    <span>{slice.value}</span>
              </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={chartCardStyle}>
          <div>
            <h2 style={chartTitleStyle}>{t('hrHome.charts.weeklyAttendance.title')}</h2>
            <p style={chartSubtitleStyle}>{t('hrHome.charts.weeklyAttendance.description')}</p>
          </div>
          <div style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyAttendanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                <XAxis dataKey="day" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ color: '#cbd5f5', fontSize: '13px' }} />
                <Bar dataKey="attendance" fill={PALETTE.green} radius={[8, 8, 0, 0]} name={t('hrHome.charts.dailyAttendance.attendance')} />
                <Bar dataKey="leave" fill={PALETTE.amber} radius={[8, 8, 0, 0]} name={t('hrHome.charts.dailyAttendance.leave')} />
                <Bar dataKey="absence" fill={PALETTE.red} radius={[8, 8, 0, 0]} name={t('hrHome.charts.dailyAttendance.absence')} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        </div>

      <div style={{
        ...chartCardStyle,
        width: '100%',
        marginTop: isMobile ? '16px' : '28px',
        height: isMobile ? '280px' : `calc(100vh - 250px)`,
        minHeight: isMobile ? 'unset' : '400px',
      }}>
        <div>
          <h2 style={chartTitleStyle}>{t('hrHome.charts.satisfactionVsPerformance.title')}</h2>
          <p style={chartSubtitleStyle}>{t('hrHome.charts.satisfactionVsPerformance.description')}</p>
        </div>
        <div style={{ height: 'calc(100% - 60px)', width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={satisfactionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="month" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ color: '#cbd5f5', fontSize: '13px' }} />
              <Line type="monotone" dataKey="satisfaction" stroke={PALETTE.teal} strokeWidth={2.3} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="performance" stroke={PALETTE.orange} strokeWidth={2.3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </motion.div>
  );
};

export default HrDashboard;
